// 批次 43（#41 GNL-005-e，裁決③）：legacy 查詢 form 右欄說明區的**文字地圖**＋泛用渲染器。
//
// legacy 形態（行號基準 ePortfolio_work，node 逐檔掃描親驗 2026-10-02）：
//   - queryPage.jsp:211-218     說明: + p(使用停用表單時…) + ol×3
//   - fieldTypeList.jsp:226-231 說明: + ol×1
//   - teacherPoolList.jsp:398-405 說明: + ol×3
//   （form-generator／form-template 的說明區含按鈕/textarea/inline input，非純文字，
//     不進本表——由 PageBehavior.asideNote hook／自訂 body 直接渲染。）
//
// 「說明:」字面（含半形冒號）是每頁共通的 leading text node；渲染器固定輸出。
// 【證據等級】§7 第 3 級（原始碼逐字）。【雛形值待實機校準】
import type { ReactElement } from 'react';
import { Fragment } from 'react';

export interface LegacyAsideBlock {
  /** 區塊前的 header 文字（如「說明:」；多段說明的頁每段各自帶）。 */
  header?: string;
  kind: 'p' | 'ol' | 'ul';
  /** kind==='p' 時的段落文字。 */
  text?: string;
  /** ol/ul 的 li 逐字清單。 */
  items?: readonly string[];
  /** items 中要掛 text-danger 的索引（formTemplate.jsp:682 警語）。 */
  danger?: readonly number[];
}

export const LEGACY_ASIDE_NOTES: Record<string, readonly LegacyAsideBlock[]> = {
  'form-co-edit-form-query-page': [
    { header: '說明:', kind: 'p', text: '使用停用表單時，此張表單不會計入表單完成度及學習護照內。' },
    {
      kind: 'ol',
      items: [
        '教學管理單位：可以查看所有人表單',
        '科部管理員：可以查看自己科部內的表單',
        '學員及簽核教師：只能查看到表單流程與自己相關的表單',
      ],
    },
  ],
  'form-template-field-type-list': [
    {
      header: '說明:',
      kind: 'ol',
      items: ['只需要新增有重複使用需要的欄位，增加太多時，會造成選取的選單出現太多項目，不易查找。'],
    },
  ],
  'instruction-teacher-pool-list': [
    {
      header: '說明:',
      kind: 'ol',
      items: ['臨床訓練師資群名單維護。', '提供學員在填表單時，可在師資群名單中選擇其評核教師。', '在同職類下，師資群名稱皆不可重複。'],
    },
  ],
  // ── 批次 44（#42 GNL-005-f）：五頁說明區（legacy raw-dom 渲染文字親撈 2026-10-02）──
  'discuss-discuss-board': [
    { header: '說明:', kind: 'ol', items: ['同訓練類別及留言類別下，留言板主題不可重複。'] },
  ],
  'discuss-discuss-type': [
    { header: '說明:', kind: 'ol', items: ['同醫院及訓練類別下，留言類別名稱不可重複。', '留言類別未被使用，才提供刪除按鈕。'] },
  ],
  'form-plan-form-dispatch': [
    {
      header: '說明:',
      kind: 'ol',
      items: [
        '補發計畫表單: 補發送今日或今日前應發之表單。',
        '補發計畫表單(每月幾_號用): 針對輸入日期來發送那日應發的表單，只發有設定[每月幾_號]的表單',
      ],
    },
    {
      header: '說明:',
      kind: 'ol',
      items: [
        '補發計畫測驗: 補發送今日或今日前應發之測驗。',
        '補發計畫測驗(每月幾_號用): 針對輸入日期來發送那日應發的表單，只發有設定[每月幾_號]的表單',
      ],
    },
  ],
  'instruction-plan-target-plan-target-list': [
    {
      header: '說明:',
      kind: 'ol',
      items: [
        '計劃中若有分組，則組內的階段、子階段的名稱需唯一；若沒分組，則整個計劃的中的階段、子階段的名稱需唯一。',
        '在每個節點最後必須要設定子階段，或是也可以只設定子階段。',
      ],
    },
  ],
  'form-plan-form-list': [
    {
      header: '說明:',
      kind: 'ol',
      items: [
        '使用[刪除]表單時，表單可能會再產生出來(表單如填過值，會被設成停用)。',
        '使用[停用]表單時，表單不會再被產生出來。',
        '[手動發送計畫表單]和自動發送功能相同，一樣是依訓練時程計算發送日之後發出，不會重複發送。',
      ],
    },
  ],
};

export function legacyAsideNotesFor(pageId: string): readonly LegacyAsideBlock[] {
  return LEGACY_ASIDE_NOTES[pageId] ?? [];
}

/** 泛用說明區渲染（每段可帶 header 文字＋p/ol/ul blocks；css class 走 react 版型慣例）。 */
export function LegacyAsideBlocks({ pageId }: { pageId: string }): ReactElement | null {
  const blocks = legacyAsideNotesFor(pageId);
  if (blocks.length === 0) return null;
  return (
    <div data-ui-id={`${pageId}-aside`}>
      {blocks.map((block, i) => (
        <Fragment key={i}>
          {block.header === undefined ? null : block.header}
          {block.kind === 'p' ? (
            <p>{block.text}</p>
          ) : block.kind === 'ol' ? (
            <ol>
              {(block.items ?? []).map((item, j) => (
                <li key={j} className={block.danger?.includes(j) ? 'text-danger' : undefined}>
                  {item}
                </li>
              ))}
            </ol>
          ) : (
            <ul>
              {(block.items ?? []).map((item, j) => (
                <li key={j} className={block.danger?.includes(j) ? 'text-danger' : undefined}>
                  {item}
                </li>
              ))}
            </ul>
          )}
        </Fragment>
      ))}
    </div>
  );
}
